import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { LicenseClaims } from "@coach/shared";
import {
  LICENSE_TOKEN_KEY,
  localStorageAdapter,
  type LicenseStorage,
} from "./cache.js";
import { verifyLicenseToken, type LicenseGateState } from "./verify.js";

interface LicenseContextValue {
  state: LicenseGateState;
  claims: LicenseClaims | null;
  /** Persists a token issued by the server and re-evaluates the gate. */
  setToken: (token: string) => Promise<void>;
  /** Wipes the cached token; useful for sign-out. */
  clear: () => Promise<void>;
  /**
   * Force the gate into a `blocked` state without touching the cached token.
   * Used by the online heartbeat when the server reports the license is no
   * longer valid (e.g. trial expired / subscription paused) even though the
   * offline JWT still has a future `exp`.
   */
  block: (reason: string) => void;
  /** Re-runs verification (e.g. after a successful online refresh). */
  refresh: () => Promise<void>;
  /**
   * Whether the current license JWT grants the given entitlement
   * `feature.<key>`. Returns `false` when no token is present or the
   * entitlements claim is absent (legacy tokens from before Phase 4).
   */
  hasFeature: (key: string) => boolean;
}

const LicenseContext = createContext<LicenseContextValue | null>(null);

export interface LicenseProviderProps {
  /** Base64-encoded SPKI PEM of the server's Ed25519 public key. */
  publicKey: string;
  storage?: LicenseStorage;
  children: ReactNode;
}

export function LicenseProvider({
  publicKey,
  storage = localStorageAdapter,
  children,
}: LicenseProviderProps) {
  const [state, setState] = useState<LicenseGateState>({ kind: "unknown" });

  const evaluate = useMemo(
    () => async () => {
      const token = await storage.get(LICENSE_TOKEN_KEY);
      if (!token) {
        setState({ kind: "unknown" });
        return;
      }
      const result = await verifyLicenseToken(token, publicKey);
      if (result.ok && result.claims) {
        const now = Math.floor(Date.now() / 1000);
        if (result.claims.exp < now) {
          setState({
            kind: "grace",
            claims: result.claims,
            graceUntil: result.claims.graceUntil,
          });
        } else {
          setState({ kind: "valid", claims: result.claims });
        }
      } else {
        setState({ kind: "blocked", reason: result.reason });
      }
    },
    [publicKey, storage],
  );

  useEffect(() => {
    void evaluate();
  }, [evaluate]);

  const value: LicenseContextValue = {
    state,
    claims:
      state.kind === "valid" || state.kind === "grace" ? state.claims : null,
    async setToken(token) {
      await storage.set(LICENSE_TOKEN_KEY, token);
      await evaluate();
    },
    async clear() {
      await storage.remove(LICENSE_TOKEN_KEY);
      setState({ kind: "unknown" });
    },
    block(reason) {
      setState({ kind: "blocked", reason });
    },
    refresh: evaluate,
    hasFeature(key) {
      const claims =
        state.kind === "valid" || state.kind === "grace" ? state.claims : null;
      if (!claims?.entitlements) return false;
      return claims.entitlements.includes(key);
    },
  };

  return (
    <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>
  );
}

export function useLicense(): LicenseContextValue {
  const ctx = useContext(LicenseContext);
  if (!ctx) throw new Error("useLicense must be used inside <LicenseProvider>");
  return ctx;
}

/**
 * Convenience hook: shorthand for `useLicense().hasFeature(key)`. Re-renders
 * the calling component when the underlying license state changes.
 */
export function useHasFeature(key: string): boolean {
  return useLicense().hasFeature(key);
}
